import { useVaults } from '../logic/useVaults'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { InfoTile } from '@/ui/molecules/info-tile/InfoTile'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { getTokenImage } from '@/ui/assets/tokens'
import { paths } from '@/config/paths'
import { Button } from '@/ui/atoms/button/Button'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { ApyWithRewardsCell } from '@/features/my-portfolio/components/ApyWithRewardsCell'
import { Percentage } from '@/domain/types/NumericValues'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { Card } from './card/Card'
import { Badge } from '@/ui/atoms/badge/Badge'
import { AlertTriangle } from 'lucide-react'

export function UsdxlVaults() {
  const { vaults, isLoading } = useVaults()
  const { getByAddress, error: pointsDataError, isLoading: isPointsDataLoading } = usePointsData()
  const hasPointsError = !isPointsDataLoading && !!pointsDataError?.message
  const router = useRouter()

  if (isLoading) {
    return <div className="text-muted-foreground">Loading vault data...</div>
  }

  return vaults.map((vault, index) => {
    const points = getByAddress({
      type: 'vault',
      action: 'deposit',
      address: vault.metadata.vaultToken.address,
    })

    return (
      <Panel.Wrapper
        key={index}
        className="box-gradient-hover flex cursor-pointer flex-row gap-4 bg-panel p-4 sm:p-8"
        onClick={() => {
          router.push(
            paths.deprecatedVaultDetails
              .replace(':chainId', vault.metadata.chainId.toString())
              .replace(':vaultAddress', vault.metadata.vaultToken.address),
          )
        }}
      >
        <div className="w-10">
          <Image
            src={getTokenImage(vault.metadata.vaultToken.symbol)}
            className="h-6 w-6 rounded-full"
            width={24}
            height={24}
            alt=""
          />
        </div>
        <Card.Content>
          <div className="flex w-44 flex-col items-start gap-2">
            <InfoTile.Label className="-mt-1">
              <div className="flex flex-col gap-1">
                <Typography variant="h2" className="text-lg" gradient>
                  {vault.metadata.vaultToken.name}
                </Typography>

                <p className="font-bold text-foreground text-xs">
                  by {vault.metadata.protocol.owner || vault.metadata.protocol.name}
                </p>
              </div>
            </InfoTile.Label>
            <InfoTile.Value className="text-muted-foreground text-xs sm:text-sm">
              {vault.metadata.description.short}
            </InfoTile.Value>
            <Badge className="flex flex-row items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-yellow-500" /> Deprecated
            </Badge>
          </div>

          <Card.ContentContainer>
            <Card.Item>
              <Card.Label>TVL</Card.Label>
              <Card.Value>{vault.metadata.vaultToken.formatUSD(vault.tvl, { compact: true })}</Card.Value>
            </Card.Item>

            <Card.Item>
              <Card.Label>Your deposit</Card.Label>
              <Card.Value>{vault.metadata.vaultToken.formatUSD(vault.userVaultBalance, { compact: true })}</Card.Value>
            </Card.Item>

            <Card.Item>
              <Card.Label>
                <ApyTooltip variant="depositVault">APY</ApyTooltip>
              </Card.Label>
              <Card.Value>
                <ApyWithRewardsCell
                  action="deposit"
                  asset={vault.metadata.vaultToken.symbol}
                  apy={Percentage(vault.apy.toNumber())}
                  points={points}
                  className="text-foreground"
                  pointsError={hasPointsError}
                  pointsLoading={isPointsDataLoading}
                  inline
                />
              </Card.Value>
            </Card.Item>

            <Card.ButtonContainer>
              <Button size="sm">Details</Button>
            </Card.ButtonContainer>
          </Card.ContentContainer>
        </Card.Content>
      </Panel.Wrapper>
    )
  })
}
